import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiServiceBase } from '@api/abstracts';
import { DataService } from '@shared/services';
import { StoresMiddleOffice, StoresMiddleOfficeCache } from '@shared/services/middle-office/models';
import { PageStore } from '@stores';
import { filter, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class MiddleOfficeService extends ApiServiceBase<StoresMiddleOffice> {
  private readonly dataService = inject(DataService);
  private readonly storesAvailables = signal<string[]>(null!);
  private readonly currentStoreUuid = inject(PageStore).selectPath((state) => state.config?.context?.uuid);
  readonly isMiddleOfficeAvailable = computed(() => {
    const storesAvailables = this.storesAvailables();
    const currentStoreUuid = this.currentStoreUuid();
    if (currentStoreUuid && storesAvailables) {
      return storesAvailables.includes(currentStoreUuid);
    }
    return false;
  });

  constructor() {
    super('');
    this.dataService
      .get$<StoresMiddleOfficeCache>('middleOfficeStores')
      .pipe(
        takeUntilDestroyed(),
        filter((cache): cache is StoresMiddleOfficeCache => cache !== null),
        tap((cache) => {
          this.storesAvailables.set(cache.storesAvailables);
        })
      )
      .subscribe();
  }

  /**
   * Resolves a middleware token to its target store URL.
   * Returns null when no store is returned or the API call fails.
   * Always emits exactly once, never throws.
   */
  resolveStoreUrl(token: string): Observable<string | null> {
    return this.getStores(token).pipe(
      take(1),
      map((stores) => {
        const url = stores.stores?.[0]?.url;
        if (url) return url;
        this.logs.error('MiddleOfficeService', 'No store found in middleware');
        this.storesAvailables.set([]);
        this.dataService.remove('middleOfficeStores').pipe(take(1)).subscribe();
        return null;
      }),
      catchError((error: unknown) => {
        // Do not pass the raw error: its HttpErrorResponse url/message carry the ?token=... query string
        this.logs.error('MiddleOfficeService', 'Failed to fetch stores from middleware', {
          status: error instanceof HttpErrorResponse ? error.status : undefined,
        });
        return of(null);
      })
    );
  }

  logout(): void {
    this.storesAvailables.set(null!);
    this.dataService.remove('middleOfficeStores').pipe(take(1)).subscribe();
  }

  private getStores(token: string): Observable<StoresMiddleOffice> {
    return this.read('userinfos', {
      token,
    }).pipe(
      switchMap((stores: StoresMiddleOffice) => {
        this.cacheSettings.enabled.set(false);
        const expirationDate = (stores.expiredate * 1000 - Date.now()) / 1000;
        const storeCache: StoresMiddleOfficeCache = {
          storesAvailables: stores.stores.reduce<string[]>((acc, val) => {
            acc.push(val.uuid);
            return acc;
          }, [] as string[]),
          token,
        };
        this.storesAvailables.set(storeCache.storesAvailables);
        return this.dataService.set('middleOfficeStores', storeCache, expirationDate ?? 0).pipe(map(() => stores));
      })
    );
  }
}
